<template>
    <section class="bond-v2-header">
        <div class="bond-v2-header__inner">
            <div class="bond-v2-header__tags" role="list" aria-label="Bond classification">
                <span 
                    v-for="tag in headerTags" 
                    :key="tag.label" 
                    class="tag" 
                    :class="[tag.className, { 'tag--clickable': tag.className === 'tag--rating' && resolvedCreditRiskMeter }]" 
                    role="listitem"
                    @click="tag.className === 'tag--rating' && resolvedCreditRiskMeter ? showCromDialog = true : null"
                >
                    <span
                        v-if="tag.ratingIcon"
                        class="tag__rating-icon"
                        aria-hidden="true"
                    >
                        <CreditRiskMeter v-if="resolvedCreditRiskMeter" :meter="resolvedCreditRiskMeter" variant="compact" compact-width="28" class="crom-header-gauge-only" :security-label="computedSecurityLabel" />
                        <img v-else :src="tag.ratingIcon" alt="" aria-hidden="true" />
                    </span>
                    <img v-else-if="tag.icon" :src="tag.icon" alt="" aria-hidden="true" width="12" height="13" style="width: 12px; height: 13px; display: block;" />
                    {{ tag.label }}
 
                    <!-- Inline info button for rating tag -->
                    <button 
                      v-if="tag.className === 'tag--rating' && resolvedCreditRiskMeter"
                      type="button" 
                      class="crom-info-btn" 
                      aria-label="About Credit Risk-o-Meter"
                      style="margin-left: 4px;"
                      @click.stop="showCromDialog = true"
                    >
                      i
                    </button>
                </span>
            </div>

            <div class="bond-v2-header__issuer" :class="{ 'bond-v2-header__issuer--no-logo': !issuerLogo }">
                <img
                    v-if="issuerLogo"
                    class="issuer-logo issuer-logo--image"
                    :src="issuerLogo"
                    :alt="`${data.issuerName} logo`"
                    @error="handleIssuerLogoError"
                />
                <div class="bond-v2-header__copy">
                    <h1 id="bond-v2-title" class="bond-v2-header__title">{{ data.issuerName }}</h1>
                    <div class="bond-v2-header__isin">ISIN: {{ data.isin }}</div>
                </div>
            </div>
        </div>
    </section>

    <!-- Dialog recursive modal inside header -->
    <v-dialog v-model="showCromDialog" max-width="840px" transition="dialog-bottom-transition">
        <v-card class="crom-dialog-card" style="position: relative; border-radius: 16px; padding: 24px; background: #fff;">
            <button 
                type="button" 
                class="crom-dialog-close-btn" 
                aria-label="Close dialog"
                style="position: absolute; top: 16px; right: 16px; background: transparent; border: none; font-size: 18px; cursor: pointer; color: #5b6784; z-index: 10;"
                @click="showCromDialog = false"
            >
                ✕
            </button>
            <v-card-text class="crom-dialog-body" style="padding: 0;">
                <CreditRiskMeter 
                    v-if="resolvedCreditRiskMeter"
                    :meter="resolvedCreditRiskMeter" 
                    variant="full" 
                    :show-disclaimer="true"
                    :security-label="computedSecurityLabel"
                />
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import seniorSecuredBondSparkleIcon from '@/assets/icons/bond-details-v2/senior-secured-bond-sparkle.svg'
import unsecuredBondSparkleIcon from '@/assets/icons/bond-details-v2/unsecured-bond-sparkle.svg'
import newIssuerSparkleIcon from '@/assets/icons/bond-details-v2/new-issuer-sparkle.svg'
import { isValidCreditRating } from '@/utils/common'
import { getRatingIcon } from '@/utils/ratingIcons'
import { useBondV2Data } from './bondV2Context'
import CreditRiskMeter from '../bonds/CreditRiskMeter.vue'

const data = useBondV2Data()
const issuerLogoLoadFailed = ref(false)
const showCromDialog = ref(false)

const resolvedCreditRiskMeter = computed(() => {
    if (data.value.creditRiskMeter) return data.value.creditRiskMeter

    const symbol = data.value.rating?.toUpperCase().trim() || ''
    if (!symbol) return null

    let level = 1
    if (/\bAAA\b|\bAA[+-]?\b/.test(symbol)) level = 1
    else if (/\bA[+-]?\b/.test(symbol)) level = 2
    else if (/\bBBB[+-]?\b/.test(symbol)) level = 3
    else if (/\bBB[+-]?\b/.test(symbol)) level = 4
    else if (/\bB[+-]?\b/.test(symbol)) level = 5
    else if (/\bC\b|\bD\b/.test(symbol)) level = 6

    return {
        state: 'OK',
        level,
        governingSymbol: data.value.rating,
        governingCra: '',
    }
})

const computedSecurityLabel = computed(() => {
    return data.value.tags.find((tag) => {
        const lowerTag = tag.toLowerCase()
        const isSecured = (lowerTag.includes('secured') && !lowerTag.includes('unsecured')) || lowerTag.includes('debenture')
        const isUnsecured = lowerTag.includes('unsecured') || lowerTag.includes('subordinate')
        return isSecured || isUnsecured
    }) || null
})

const issuerLogo = computed(() => issuerLogoLoadFailed.value ? '' : data.value.issuerLogo || '')

watch(() => data.value.issuerLogo, () => {
    issuerLogoLoadFailed.value = false
})

const handleIssuerLogoError = () => {
    issuerLogoLoadFailed.value = true
}

const headerTags = computed(() => data.value.tags.map((tag) => {
    const ratingIcon = isValidCreditRating(tag) ? getRatingIcon(tag) : null

    if (ratingIcon) {
        return {
            label: tag,
            icon: '',
            ratingIcon,
            className: 'tag--rating'
        }
    }

    if (tag === 'NCD') {
        return {
            label: tag,
            icon: '',
            ratingIcon: '',
            className: 'tag--type'
        }
    }

    const lowerTag = tag.toLowerCase()
    const isSecured = (lowerTag.includes('secured') && !lowerTag.includes('unsecured')) || lowerTag.includes('debenture')
    const isUnsecured = lowerTag.includes('unsecured') || lowerTag.includes('subordinate')

    if (isSecured || tag === 'Senior Secured Bond') {
        return {
            label: tag,
            icon: seniorSecuredBondSparkleIcon,
            ratingIcon: '',
            className: 'tag--green'
        }
    }

    if (isUnsecured) {
        return {
            label: tag,
            icon: unsecuredBondSparkleIcon,
            ratingIcon: '',
            className: 'tag--red'
        }
    }

    if (tag === 'New Issuer') {
        return {
            label: tag,
            icon: newIssuerSparkleIcon,
            ratingIcon: '',
            className: 'tag--new-issuer'
        }
    }

    return {
        label: tag,
        icon: '',
        ratingIcon: '',
        className: ''
    }
}))
</script>

<style scoped>
.bond-v2-header {
    width: 100vw;
    height: 95px;
    margin-left: calc(50% - 50vw);
    background: var(--yello, linear-gradient(180deg, #fff 25.96%, #fffbe0 100%));
    border: none;
    border-radius: 0;
    padding: 0;
    display: block;
    box-sizing: border-box;
}

.bond-v2-header__inner {
    width: 100%;
    max-width: 1440px;
    height: 100%;
    margin: 0 auto;
    position: relative;
    padding: 0 24px;
    box-sizing: border-box;
}

.bond-v2-header__issuer {
    position: absolute;
    left: 24px;
    top: 39px;
    height: 44px;
    display: flex;
    align-items: center;
    gap: 2px;
}

.issuer-logo {
    width: 24px;
    height: 24px;
    border-radius: 3px;
    background: #fff;
    border: none;
    flex: 0 0 auto;
}

.issuer-logo--image {
    display: block;
    object-fit: contain;
}

.bond-v2-header__copy {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    white-space: nowrap;
}

.bond-v2-header__issuer--no-logo .bond-v2-header__copy {
    padding-left: 8px;
}

.bond-v2-header__title {
    margin: 0;
    color: #101828;
    font-family: var(--font-family-primary);
    font-size: 17px;
    line-height: 24px;
    font-weight: 600;
    text-transform: capitalize;
}

.bond-v2-header__isin {
    color: var(--bond-v2-text, var(--color-text-primary));
    font-size: 13px;
    line-height: 12px;
    font-weight: 500;
}

.bond-v2-header__tags {
    position: absolute;
    top: 0;
    left: 29.5px;
    right: 24px;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 4px;
}

.tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 26px;
    padding: 4px 6px;
    border: none;
    border-top: 1px solid #eff9ff;
    border-radius: 0 0 4px 4px;
    background: #eff9ff;
    color: #005dab;
    font-size: 15px;
    line-height: normal;
    font-weight: 500;
    gap: 2px;
}

.tag--clickable {
    cursor: pointer;
}

/* Info Circle Icon Button */
.crom-info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #005dab;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.2s;
  vertical-align: middle;
}

.crom-info-btn:hover {
  background-color: #0a2fa5;
}

.tag + .tag {
    border-left: 1px solid #eff9ff;
}

.tag > img {
    width: 12px;
    height: 13px;
    display: block;
}

.tag__rating-icon {
    width: 30px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 30px;
}

.tag__rating-icon img {
    display: block;
    width: 30px;
    height: auto;
}

.tag--senior-secured {
    margin-left: auto;
    background: #e9ffe6;
    color: #006e35;
}

.tag--green {
    margin-left: auto;
    background: #e9ffe6;
    color: #006e35;
}

.tag--red {
    margin-left: auto;
    background: #f9dedc;
    color: #b3261e;
}

.tag--new-issuer {
    background: #e6f6ff;
    color: #0063b1;
}

@media (max-width: 640px) {
    .bond-v2-header {
        width: 100%;
        height: auto;
        min-height: 76px;
        margin-left: 0;
        padding-bottom: 8px;
    }

    .bond-v2-header__inner {
        height: auto;
        padding: 0 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .bond-v2-header__issuer {
        position: static;
        height: auto;
        gap: 6px;
        padding-top: 0;
    }

    .bond-v2-header__tags {
        position: static;
        gap: 4px;
        align-items: flex-start;
        width: 100%;
    }

    .tag--senior-secured {
        margin-left: auto;
    }

    .tag {
        min-height: 18px;
        padding: 3px 5px;
        font-size: 11px;
        line-height: 12px;
        gap: 2px;
    }

    .tag > img {
        width: 10px;
        height: 11px;
    }

    .tag__rating-icon {
        width: 24px;
        height: 13px;
        flex-basis: 24px;
    }

    .tag__rating-icon img {
        width: 24px;
    }

    .issuer-logo {
        width: 20px;
        height: 20px;
    }

    .bond-v2-header__copy {
        min-width: 0;
        white-space: normal;
    }

    .bond-v2-header__issuer--no-logo .bond-v2-header__copy {
        padding-left: 4px;
    }

    .bond-v2-header__title {
        font-size: 13px;
        line-height: 17px;
    }

    .bond-v2-header__isin {
        font-size: 10px;
        line-height: 13px;
    }
}

.crom-header-gauge-only :deep(.crom-compact-info) {
  display: none !important;
}

.crom-header-gauge-only :deep(.crom-compact-svg) {
  align-items: center !important;
}
</style>